import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useScope } from "@/context/ScopeContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Building2, RefreshCw, Upload, School, BarChart3, Droplets, Bath, AlertTriangle, CheckCircle2, XCircle, Heart, Users, Wrench, ShieldCheck, TrendingUp, Brain } from "lucide-react";
import { toast } from "sonner";
import ExportPanel from "@/components/ExportPanel";
import ClassroomsToiletsInfrastructureTab from "@/components/ClassroomsToiletsInfrastructureTab";
import ClassroomsToiletsToiletWaterTab from "@/components/ClassroomsToiletsToiletWaterTab";
import ClassroomsToiletsHygieneWashTab from "@/components/ClassroomsToiletsHygieneWashTab";
import ClassroomsToiletsEquityInclusionTab from "@/components/ClassroomsToiletsEquityInclusionTab";
import ClassroomsToiletsRiskActionTab from "@/components/ClassroomsToiletsRiskActionTab";
import ClassroomsToiletsInsightsTab from "@/components/ClassroomsToiletsInsightsTab";
import ClassroomsToiletsGuidanceChip from "@/components/ClassroomsToiletsGuidanceChip";
import { useClassroomsToiletsAiGuidance } from "@/hooks/useClassroomsToiletsAiGuidance";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie, Legend, LineChart, Line, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from "recharts";

import { getBackendUrl } from "@/lib/backend";
import "./ClassroomsToiletsDashboard.css";

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

/** UDISE School_Type_Code: 1 = boys only, 2 = girls only, 3 = co-ed. Unknown → co-ed. */
function normalizeSchoolTypeCode(code) {
  const n = Number(code);
  if (n === 1 || n === 2 || n === 3) return n;
  return 3;
}

function inferSchoolTypeFromName(name) {
  if (name == null || String(name).trim() === "") return null;
  const s = String(name).toLowerCase();
  const boysLike = /\b(boys?|balak)\b/i.test(s);
  const girlsLike = /\b(girls?|kanya|balika|kanyashala)\b/i.test(s);
  if (boysLike && !girlsLike) return 1;
  if (girlsLike && !boysLike) return 2;
  return null;
}

/** Prefer API effective_school_type, then UDISE code, then name heuristics (matches backend). */
function normalizeEffectiveSchoolType(school) {
  const eff = school?.effective_school_type;
  if (eff !== undefined && eff !== null && eff !== "") {
    const n = Number(eff);
    if (n === 1 || n === 2 || n === 3) return n;
  }
  const fromCode = normalizeSchoolTypeCode(school?.school_type_code);
  if (fromCode !== 3) return fromCode;
  const inferred = inferSchoolTypeFromName(school?.school_name);
  if (inferred !== null) return inferred;
  return 3;
}

const KPICard = ({ label, value, suffix = "", icon: Icon, color = "blue", description, trend }) => {
  const colors = { 
    blue: "bg-blue-50 text-blue-600", 
    green: "bg-emerald-50 text-emerald-600", 
    red: "bg-red-50 text-red-600", 
    amber: "bg-amber-50 text-amber-600", 
    purple: "bg-purple-50 text-purple-600",
    cyan: "bg-cyan-50 text-cyan-600"
  };
  return (
    <Card className="border-slate-200" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</p>
            <div className="flex items-baseline gap-1 mt-1">
              <span className="text-2xl font-bold text-slate-900 tabular-nums">
                {typeof value === 'number' ? value.toLocaleString('en-IN') : value}
              </span>
              {suffix && <span className="text-lg text-slate-500">{suffix}</span>}
            </div>
            {description && <p className="text-xs text-slate-400 mt-1">{description}</p>}
            {trend && <p className={`text-xs mt-1 ${trend > 0 ? 'text-emerald-500' : 'text-red-500'}`}>{trend > 0 ? '↑' : '↓'} {Math.abs(trend)}%</p>}
          </div>
          {Icon && <div className={`p-2 rounded-lg ${colors[color]}`}><Icon className="w-5 h-5" strokeWidth={1.5} /></div>}
        </div>
      </CardContent>
    </Card>
  );
};

const GaugeChart = ({ value, label, color = "#10b981" }) => {
  const getColor = (val) => val >= 90 ? "#10b981" : val >= 80 ? "#f59e0b" : "#ef4444";
  const actualColor = color || getColor(value);
  const circumference = 2 * Math.PI * 45;
  const strokeDasharray = `${(value / 100) * circumference} ${circumference}`;
  return (
    <div className="flex flex-col items-center">
      <div className="relative w-28 h-28">
        <svg className="w-full h-full transform -rotate-90">
          <circle cx="56" cy="56" r="45" stroke="#e2e8f0" strokeWidth="10" fill="none" />
          <circle cx="56" cy="56" r="45" stroke={actualColor} strokeWidth="10" fill="none" strokeDasharray={strokeDasharray} strokeLinecap="round" />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-xl font-bold" style={{ color: actualColor }}>{value}%</span>
        </div>
      </div>
      <p className="text-sm text-slate-600 mt-2 text-center max-w-[100px]">{label}</p>
    </div>
  );
};

const ProgressBar = ({ label, value, max, color = "bg-emerald-500" }) => {
  const pct = Math.round((value / max) * 100);
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span className="text-slate-600">{label}</span>
        <span className="font-medium">{pct}%</span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const ClassroomsToiletsDashboard = () => {
  const { scope } = useScope();
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [overview, setOverview] = useState(null);
  const [blockData, setBlockData] = useState([]);
  const [classroomCondition, setClassroomCondition] = useState(null);
  const [toiletDistribution, setToiletDistribution] = useState(null);
  const [hygieneMetrics, setHygieneMetrics] = useState(null);
  const [riskSchools, setRiskSchools] = useState(null);
  const [constructionStatus, setConstructionStatus] = useState(null);
  const [equityMetrics, setEquityMetrics] = useState(null);
  const [topBottomBlocks, setTopBottomBlocks] = useState(null);
  const [activeTab, setActiveTab] = useState("infrastructure");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Build scope parameters - the axios interceptor will merge them with localStorage values
      // Also pass district_name, block_name, school_name to help with matching
      const requestParams = {};
      if (scope.districtCode && scope.districtCode.trim()) {
        requestParams.district_code = scope.districtCode.trim();
      }
      if (scope.districtName && scope.districtName.trim()) {
        requestParams.district_name = scope.districtName.trim();
      }
      if (scope.blockCode && scope.blockCode.trim()) {
        requestParams.block_code = scope.blockCode.trim();
      }
      if (scope.blockName && scope.blockName.trim()) {
        requestParams.block_name = scope.blockName.trim();
      }
      if (scope.udiseCode && scope.udiseCode.trim()) {
        requestParams.udise_code = scope.udiseCode.trim();
      }
      if (scope.schoolName && scope.schoolName.trim()) {
        requestParams.school_name = scope.schoolName.trim();
      }

      console.log('Fetching Classrooms & Toilets data with scope:', {
        districtCode: scope.districtCode,
        blockCode: scope.blockCode,
        udiseCode: scope.udiseCode,
        params: requestParams,
        apiUrl: API
      });

      const [overviewRes, blockRes, conditionRes, toiletRes, hygieneRes, riskRes, constructionRes, equityRes, rankRes] = await Promise.all([
        axios.get(`${API}/classrooms-toilets/overview`, { params: requestParams }),
        axios.get(`${API}/classrooms-toilets/block-wise`, { params: requestParams }),
        axios.get(`${API}/classrooms-toilets/classroom-condition`, { params: requestParams }),
        axios.get(`${API}/classrooms-toilets/toilet-distribution`, { params: requestParams }),
        axios.get(`${API}/classrooms-toilets/hygiene-metrics`, { params: requestParams }),
        axios.get(`${API}/classrooms-toilets/risk-schools`, { params: requestParams }),
        axios.get(`${API}/classrooms-toilets/construction-status`, { params: requestParams }),
        axios.get(`${API}/classrooms-toilets/equity-metrics`, { params: requestParams }),
        axios.get(`${API}/classrooms-toilets/top-bottom-blocks`, { params: requestParams })
      ]);
      
      console.log('Classrooms & Toilets data fetched successfully:', {
        overviewTotalSchools: overviewRes?.data?.total_schools,
        overviewTotalClassrooms: overviewRes?.data?.total_classrooms,
        blockDataCount: Array.isArray(blockRes?.data) ? blockRes.data.length : 0,
        hasConditionData: !!conditionRes?.data,
        hasToiletData: !!toiletRes?.data
      });
      
      setOverview(overviewRes.data);
      setBlockData(Array.isArray(blockRes.data) ? blockRes.data : []);
      setClassroomCondition(conditionRes.data || null);
      setToiletDistribution(toiletRes.data || null);
      setHygieneMetrics(hygieneRes.data || null);
      setRiskSchools(riskRes.data || null);
      setConstructionStatus(constructionRes.data || null);
      setEquityMetrics(equityRes.data || null);
      setTopBottomBlocks(rankRes.data || null);
    } catch (error) {
      console.error("Error fetching Classrooms & Toilets data:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url,
        code: error.code
      });
      
      // Handle network errors (including ERR_BLOCKED_BY_CLIENT from browser extensions)
      if (error.code === 'ERR_NETWORK' || error.message?.includes('ERR_BLOCKED_BY_CLIENT') || error.message?.includes('Failed to fetch')) {
        console.warn("Network error detected - may be caused by browser extension");
        toast.error("Network error: Please disable ad blockers or privacy extensions and try again");
        setLoading(false);
        return;
      }
      
      const errorMsg = error.response?.data?.detail || error.message || "Failed to load dashboard data";
      
      // Only show error toast for actual errors, not for empty results
      if (error.response?.status && error.response?.status !== 404 && error.response?.status >= 500) {
        toast.error(errorMsg);
      }
      
      // Reset data on error to show "No Data" message (but not on network errors)
      if (error.response) {
        setOverview(null);
        setBlockData([]);
        setClassroomCondition(null);
        setToiletDistribution(null);
        setHygieneMetrics(null);
        setRiskSchools(null);
        setConstructionStatus(null);
        setEquityMetrics(null);
        setTopBottomBlocks(null);
      }
    } finally {
      setLoading(false);
    }
  }, [
    scope.version,
    scope.districtCode,
    scope.blockCode,
    scope.udiseCode,
    scope.districtName,
    scope.blockName,
    scope.schoolName,
  ]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleImport = async () => {
    setImporting(true);
    try {
      await axios.post(`${API}/classrooms-toilets/import`);
      toast.success("Import started! Data will be ready shortly.");
      setTimeout(() => { fetchData(); setImporting(false); }, 12000);
    } catch (error) {
      toast.error("Import failed: " + (error.response?.data?.detail || error.message));
      setImporting(false);
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-slate-900 text-white p-3 rounded-lg shadow-lg">
          <p className="font-medium">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>{entry.name}: {typeof entry.value === 'number' ? entry.value.toLocaleString() : entry.value}</p>
          ))}
        </div>
      );
    }
    return null;
  };

  const scopeKey = scope.version ?? `${scope.districtCode}-${scope.blockCode}-${scope.udiseCode}`;

  const {
    guidance,
    provider: guidanceProvider,
    loading: guidanceLoading,
    generatedAt: guidanceGeneratedAt,
  } = useClassroomsToiletsAiGuidance({
    overview,
    riskSchools,
    equityMetrics,
    hygieneMetrics,
    blockData,
    scopeLabel:
      scope.schoolName?.trim() ||
      scope.blockName?.trim() ||
      scope.districtName?.trim() ||
      "Pune District",
    scopeKey,
  });

  if (loading) return <div className="flex items-center justify-center h-96"><div className="loading-spinner" /></div>;

  // Check if we have data - overview should exist and have meaningful data
  const hasData = overview && typeof overview === 'object' && (
    (overview.total_schools > 0) || 
    (overview.total_classrooms > 0) ||
    (Object.keys(overview).length > 3)
  );
  
  // Debug log to help diagnose data loading issues
  if (overview) {
    console.log('Classrooms & Toilets Dashboard data check:', {
      hasData,
      totalSchools: overview.total_schools,
      totalClassrooms: overview.total_classrooms,
      overviewKeys: Object.keys(overview).length,
      overviewSample: Object.keys(overview).slice(0, 5)
    });
  }

  const scopeLabel =
    scope.schoolName?.trim() ||
    scope.blockName?.trim() ||
    scope.districtName?.trim() ||
    "Pune District";

  const tabItems = [
    { id: "infrastructure", label: "Infrastructure", icon: Building2 },
    { id: "toilets", label: "Toilets & Water", icon: Bath },
    { id: "hygiene", label: "Hygiene & WASH", icon: Droplets },
    { id: "equity", label: "Equity & Inclusion", icon: Users },
    { id: "risk", label: "Risk & Action", icon: AlertTriangle },
    { id: "insights", label: "Insights", icon: Brain },
  ];

  const headerByTab = {
    infrastructure: {
      eyebrowIcon: Building2,
      eyebrow: `Infrastructure & WASH Compliance · AY 2025-26 · ${scopeLabel}`,
      title: "Classrooms & Toilets Dashboard",
      description:
        "Monitor classroom condition, repair backlog, asset readiness and WASH compliance with a clearer action-first view for district and block officers.",
      ghostActions: false,
    },
    toilets: {
      eyebrowIcon: Droplets,
      eyebrow: `WASH compliance · ${scopeLabel} · 2025–26`,
      title: "Toilet & Water Dashboard",
      description:
        "Decision-ready monitoring for toilet availability, functional status, water coverage, gender-wise access, zero-toilet schools and construction pipeline.",
      ghostActions: true,
    },
    hygiene: {
      eyebrowIcon: BarChart3,
      eyebrow: `Infrastructure & WASH · AY 2025–26 · ${scopeLabel}`,
      title: "Classrooms & Toilets Dashboard",
      description:
        "Hygiene readiness view combining handwash coverage, sanitary pad access, electricity availability, toilet water coverage and block-wise WASH performance.",
      ghostActions: true,
    },
    equity: {
      eyebrowIcon: Users,
      eyebrow: `Infrastructure & WASH Compliance · AY 2025–26 · ${scopeLabel}`,
      title: "Classrooms & Toilets Dashboard",
      description: `Infrastructure & WASH Compliance • AY 2025–26 • ${scopeLabel}`,
      ghostActions: true,
      hideEyebrow: true,
    },
    risk: {
      eyebrowIcon: AlertTriangle,
      eyebrow: `Infrastructure & WASH Compliance · AY 2025–26 · ${scopeLabel}`,
      title: "Classrooms & Toilets Dashboard",
      description: `Infrastructure & WASH Compliance • AY 2025–26 • ${scopeLabel}`,
      ghostActions: true,
      hideEyebrow: true,
    },
    insights: {
      eyebrowIcon: Brain,
      eyebrow: `Infrastructure & WASH Compliance · AY 2025–26 · ${scopeLabel}`,
      title: "Classrooms & Toilets Dashboard",
      description: `Infrastructure & WASH Compliance • AY 2025–26 • ${scopeLabel}`,
      ghostActions: true,
      hideEyebrow: true,
    },
  };

  const header = headerByTab[activeTab] ?? headerByTab.infrastructure;
  const HeaderEyebrowIcon = header.eyebrowIcon;

  return (
    <div className="ct-dashboard animate-fade-in" data-testid="classrooms-toilets-dashboard">
      <section className="ct-page-header">
        <div>
          {!header.hideEyebrow && (
            <div className="ct-eyebrow">
              <HeaderEyebrowIcon size={16} />
              {header.eyebrow}
            </div>
          )}
          <h1>{header.title}</h1>
          <p>{header.description}</p>
          {hasData && activeTab === "insights" && (
            <div className="ct-status-row" style={{ marginTop: "0.75rem" }}>
              <ClassroomsToiletsGuidanceChip provider={guidanceProvider} loading={guidanceLoading} />
            </div>
          )}
        </div>
        <div className="ct-header-actions">
          {header.ghostActions ? (
            <>
              <button
                type="button"
                className="ct-ghost-action"
                onClick={handleImport}
                disabled={importing}
                data-testid="import-btn"
              >
                {importing ? <RefreshCw size={16} className="animate-spin" /> : <Upload size={16} />}
                Import Data
              </button>
              <ExportPanel
                dashboardName="classrooms-toilets"
                dashboardTitle="Classrooms & Toilets"
                ghostAction
              />
            </>
          ) : (
            <>
              <button
                type="button"
                className="ct-icon-button"
                title="Import"
                onClick={handleImport}
                disabled={importing}
                data-testid="import-btn"
              >
                {importing ? <RefreshCw size={18} className="animate-spin" /> : <Upload size={18} />}
              </button>
              <ExportPanel
                dashboardName="classrooms-toilets"
                dashboardTitle="Classrooms & Toilets"
                iconOnly
              />
            </>
          )}
          <button type="button" className="ct-primary-action" onClick={fetchData} data-testid="refresh-btn">
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </section>

      {!hasData ? (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <Building2 className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No Data Available</h3>
            <p className="text-slate-500 mb-4">Click "Import Data" to load Classrooms & Toilets data</p>
            <Button onClick={handleImport} disabled={importing} data-testid="import-empty-btn">{importing ? "Importing..." : "Import Data"}</Button>
          </CardContent>
        </Card>
      ) : (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <div className="ct-tabs-wrap" aria-label="Classrooms and toilets sections">
            <nav className="ct-tabs">
              {tabItems.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  type="button"
                  className={`ct-tab ${activeTab === id ? "active" : ""} ${id === "risk" && activeTab === id ? "risk-active" : ""} ${id === "insights" && activeTab === id ? "insights-active" : ""}`}
                  onClick={() => setActiveTab(id)}
                  data-testid={`tab-${id}`}
                >
                  <Icon size={18} />
                  {label}
                </button>
              ))}
            </nav>
          </div>

          <TabsContent value="infrastructure" className="mt-0">
            <ClassroomsToiletsInfrastructureTab
              overview={overview}
              blockData={blockData}
              classroomCondition={classroomCondition}
              scopeLabel={scopeLabel}
            />
          </TabsContent>

          <TabsContent value="toilets" className="mt-0">
            <ClassroomsToiletsToiletWaterTab
              overview={overview}
              blockData={blockData}
              toiletDistribution={toiletDistribution}
              constructionStatus={constructionStatus}
              scopeLabel={scopeLabel}
            />
          </TabsContent>

          <TabsContent value="hygiene" className="mt-0">
            <ClassroomsToiletsHygieneWashTab
              overview={overview}
              blockData={blockData}
              hygieneMetrics={hygieneMetrics}
              scopeLabel={scopeLabel}
            />
          </TabsContent>

          <TabsContent value="equity" className="mt-0">
            <ClassroomsToiletsEquityInclusionTab
              overview={overview}
              equityMetrics={equityMetrics}
              blockData={blockData}
              scopeLabel={scopeLabel}
            />
          </TabsContent>

          <TabsContent value="risk" className="mt-0">
            <ClassroomsToiletsRiskActionTab
              riskSchools={riskSchools}
              topBottomBlocks={topBottomBlocks}
              blockData={blockData}
            />
          </TabsContent>
          <TabsContent value="insights" className="mt-0">
            <ClassroomsToiletsInsightsTab
              overview={overview}
              riskSchools={riskSchools}
              equityMetrics={equityMetrics}
              hygieneMetrics={hygieneMetrics}
              blockData={blockData}
              guidance={guidance?.insights}
              guidanceProvider={guidanceProvider}
              guidanceLoading={guidanceLoading}
              guidanceGeneratedAt={guidanceGeneratedAt}
            />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default ClassroomsToiletsDashboard;
